import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { getEOMData, markAsSaved, ignoreEOM, EOMData } from '../api/budgets';
import LoadingSpinner from './LoadingSpinner';

interface EOMOverlayProps {
  year: number;
  month: number;
  onClose: () => void;
  onDataUpdated?: () => void;
}

export function EOMOverlay({ year, month, onClose, onDataUpdated }: EOMOverlayProps) {
  const [data, setData] = useState<EOMData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAsSaved, setMarkingAsSaved] = useState<string[]>([]);

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const eomData = await getEOMData(year, month);
      setData(eomData);
    } catch (err: any) {
      setError(err.message || 'Failed to load EOM data');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleMarkAsSaved = async (budgetIds: string[]) => {
    try {
      setMarkingAsSaved(budgetIds);
      await markAsSaved(budgetIds, year, month);
      // Reload data
      await loadData();
      if (onDataUpdated) {
        onDataUpdated();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to mark as saved');
    } finally {
      setMarkingAsSaved([]);
    }
  };

  const handleClose = async () => {
    try {
      await ignoreEOM(year, month);
      onClose();
    } catch (err: any) {
      // Still close even if marking as reviewed fails
      onClose();
    }
  };

  const renderIcon = (icon: string) => {
    if (icon.startsWith('fas fa-') || icon.startsWith('far fa-') || icon.startsWith('fab fa-')) {
      return <i className={icon}></i>;
    }
    return icon;
  };

  const monthName = new Date(year, month).toLocaleString('default', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col w-full max-w-4xl mx-auto">
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col w-full max-w-4xl mx-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">End of Month Review</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error || 'Failed to load EOM data'}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { expenses, expenseFunds, savings, overallSavings, suggestedSavings, overallNet } = data;

  // Determine progress bar colors
  const expenseProgressColor = expenses.totalRemaining < 0 
    ? 'bg-red-500' 
    : expenses.percentageRemaining <= 15 
    ? 'bg-yellow-500' 
    : 'bg-green-500';

  const savingsProgressColor = 'bg-green-500';

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col w-full max-w-4xl mx-auto overflow-y-auto">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 bg-white sticky top-0 z-10">
        <h2 className="text-2xl font-semibold text-gray-900">{monthName} Review</h2>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 space-y-8 pb-28">
        {/* Expenses Progress Bar */}
        <div>
          <div className="flex justify-between items-end mb-2">
            <div className="text-lg font-medium text-gray-700">Expenses</div>
            <div className="text-base font-semibold">
              {expenses.totalRemaining < 0 ? (
                <span className="text-red-600">
                  ${Math.abs(expenses.totalRemaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} overspent
                </span>
              ) : (
                <span className="text-gray-600">
                  ${expenses.totalRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} remaining
                </span>
              )}
            </div>
          </div>
          <div className="w-full h-3 bg-gray-300 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${expenseProgressColor}`}
              style={{
                width: `${Math.min(100, Math.max(0, 100 - expenses.percentageRemaining))}%`
              }}
            />
          </div>
        </div>

        {/* Savings Progress Bar */}
        <div>
          <div className="flex justify-between items-end mb-2">
            <div className="text-lg font-medium text-gray-700">Savings</div>
            <div className="text-base font-semibold text-gray-600">
              ${overallSavings.totalRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} remaining
            </div>
          </div>
          <div className="w-full h-3 bg-gray-300 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${savingsProgressColor}`}
              style={{
                width: `${Math.min(100, Math.max(0, overallSavings.percentage))}%`
              }}
            />
          </div>
        </div>

        {/* Expenses Section */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Expenses</h3>
          
          {/* Over Budget Items */}
          {expenses.budgets.filter(b => b.isOverBudget).length > 0 && (
            <div className="mb-6">
              <div className="text-sm font-semibold text-red-600 mb-2">
                Over Budget ({expenses.budgets.filter(b => b.isOverBudget).length})
              </div>
              <div className="space-y-2 mb-4">
                {expenses.budgets
                  .filter(b => b.isOverBudget)
                  .map(budget => (
                    <div key={budget.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm">
                          {renderIcon(budget.icon)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{budget.name}</div>
                          <div className="text-sm text-gray-500">
                            ${budget.spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${budget.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                      <div className="text-red-600 font-semibold">
                        ${budget.overBudgetAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  ))}
              </div>
              <div className="text-right font-semibold text-red-600 text-lg">
                Total Over Budget: ${expenses.totalOverBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}

          {/* Underutilized Items */}
          {expenses.budgets.filter(b => b.percentageRemaining > 30).length > 0 && (
            <div className="mb-6">
              <div className="text-sm font-semibold text-gray-600 mb-2">
                Underutilized ({expenses.budgets.filter(b => b.percentageRemaining > 30).length})
              </div>
              <div className="space-y-2 mb-4">
                {expenses.budgets
                  .filter(b => b.percentageRemaining > 30)
                  .map(budget => (
                    <div key={budget.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm">
                          {renderIcon(budget.icon)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{budget.name}</div>
                          <div className="text-sm text-gray-500">
                            ${budget.spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${budget.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                      <div className="text-gray-600 font-semibold">
                        ${budget.underutilizedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unused
                      </div>
                    </div>
                  ))}
              </div>
              <div className="text-right font-semibold text-gray-600 text-lg">
                Total Underutilized: ${expenses.totalUnderutilized.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}

          {/* Net Amount */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-lg font-semibold text-gray-900">Net Amount</div>
              <div className={`text-xl font-bold ${expenses.totalRemaining < 0 ? 'text-red-600' : expenses.totalRemaining === 0 ? 'text-gray-600' : 'text-green-600'}`}>
                {expenses.totalRemaining < 0 ? '-' : ''}${Math.abs(expenses.totalRemaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>

         {/* Expense Funds Section */}
         <div className="bg-gray-50 rounded-xl p-6">
           <h3 className="text-xl font-semibold text-gray-900 mb-4">Expense Funds</h3>
           
           {/* Negative Expense Funds */}
           {expenseFunds.budgets.filter(b => b.isNegative).length > 0 && (
             <div className="mb-6">
               <div className="text-sm font-semibold text-red-600 mb-2">
                 Negative Balance ({expenseFunds.budgets.filter(b => b.isNegative).length})
               </div>
               <div className="space-y-2">
                 {expenseFunds.budgets
                   .filter(b => b.isNegative)
                   .map(budget => (
                     <div key={budget.id} className="bg-white rounded-lg p-3 flex items-center justify-between">
                       <div className="flex items-center gap-3">
                         <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm">
                           {renderIcon(budget.icon)}
                         </div>
                         <div>
                           <div className="font-medium text-gray-900">{budget.name}</div>
                           <div className="text-sm text-gray-500">
                             ${budget.monthlySavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} saved this month
                           </div>
                         </div>
                       </div>
                       <div className="text-red-600 font-semibold">
                         ${Math.abs(budget.netAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} underfunded
                       </div>
                     </div>
                   ))}
               </div>
             </div>
           )}
         </div>

         {/* Savings Section */}
         <div className="bg-gray-50 rounded-xl p-6">
           <h3 className="text-xl font-semibold text-gray-900 mb-4">Savings</h3>
           
           {/* Regular Savings Budgets */}
           {savings.budgets.filter(b => b.leftToSave > 0.01).length > 0 && (
             <div className="mb-6">
               <div className="flex items-center justify-between mb-4">
                 <div className="text-sm font-semibold text-gray-600">
                   Savings Budgets ({savings.budgets.filter(b => b.leftToSave > 0.01).length})
                 </div>
                 <button
                   onClick={() => handleMarkAsSaved(savings.budgets.filter(b => b.leftToSave > 0.01).map(b => b.id))}
                   disabled={markingAsSaved.length > 0}
                   className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                 >
                   {markingAsSaved.some(id => savings.budgets.filter(b => b.leftToSave > 0.01).map(b => b.id).includes(id)) ? 'Marking...' : 'Mark as Saved'}
                 </button>
               </div>
               <div className="space-y-2 mb-4">
                 {savings.budgets
                   .filter(b => b.leftToSave > 0.01)
                   .map(budget => (
                     <div key={budget.id} className="bg-white rounded-lg p-3">
                       <div className="flex items-center justify-between mb-2">
                         <div className="flex items-center gap-3">
                           <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm">
                             {renderIcon(budget.icon)}
                           </div>
                           <div>
                             <div className="font-medium text-gray-900">{budget.name}</div>
                             <div className="text-sm text-gray-500">
                               ${budget.monthlySavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${budget.monthlyGoal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                             </div>
                           </div>
                         </div>
                         <div className="text-emerald-700 font-semibold">
                           ${budget.leftToSave.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left
                         </div>
                       </div>
                       <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                         <div
                           className="h-full bg-green-500 transition-all"
                           style={{
                             width: `${Math.min(100, Math.max(0, budget.percentageSaved))}%`
                           }}
                         />
                       </div>
                     </div>
                   ))}
               </div>
             </div>
           )}

           {/* Expense Funds Needing Savings */}
           {expenseFunds.budgets.filter(b => b.leftToSave > 0.01).length > 0 && (
             <div className="mb-6">
               <div className="flex items-center justify-between mb-4">
                 <div className="text-sm font-semibold text-gray-600">
                   Expense Funds ({expenseFunds.budgets.filter(b => b.leftToSave > 0.01).length})
                 </div>
                 <button
                   onClick={() => handleMarkAsSaved(expenseFunds.budgets.filter(b => b.leftToSave > 0.01).map(b => b.id))}
                   disabled={markingAsSaved.length > 0}
                   className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                 >
                   {markingAsSaved.some(id => expenseFunds.budgets.filter(b => b.leftToSave > 0.01).map(b => b.id).includes(id)) ? 'Marking...' : 'Mark as Saved'}
                 </button>
               </div>
               <div className="space-y-2 mb-4">
                 {expenseFunds.budgets
                   .filter(b => b.leftToSave > 0.01)
                   .map(budget => (
                     <div key={budget.id} className="bg-white rounded-lg p-3">
                       <div className="flex items-center justify-between mb-2">
                         <div className="flex items-center gap-3">
                           <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm">
                             {renderIcon(budget.icon)}
                           </div>
                           <div>
                             <div className="font-medium text-gray-900">{budget.name}</div>
                             <div className="text-sm text-gray-500">
                               ${budget.monthlySavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${budget.monthlyGoal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                             </div>
                           </div>
                         </div>
                         <div className="text-emerald-700 font-semibold">
                           ${budget.leftToSave.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left
                         </div>
                       </div>
                       <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                         <div
                           className="h-full bg-green-500 transition-all"
                           style={{
                             width: `${Math.min(100, Math.max(0, budget.percentageSaved))}%`
                           }}
                         />
                       </div>
                     </div>
                   ))}
               </div>
             </div>
           )}

           {/* Totals */}
           <div className="pt-4 border-t border-gray-300">
             <div className="flex justify-between items-center mb-2">
               <div className="text-lg font-semibold text-gray-900">Total Saved</div>
               <div className="text-lg font-bold text-green-600">
                 ${overallSavings.totalSaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
               </div>
             </div>
             <div className="flex justify-between items-center">
               <div className="text-lg font-semibold text-gray-900">Total Remaining</div>
               <div className="text-lg font-bold text-emerald-700">
                 ${overallSavings.totalRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
               </div>
             </div>
           </div>
         </div>

        {/* Suggested Savings */}
        {suggestedSavings !== null && suggestedSavings > 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-grey-900 mb-2">Suggested Additional Savings</h3>
            <p className="text-grey-700 mb-4">
              Consider saving an additional <span className="font-semibold">${suggestedSavings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> to offset the amount you went over budget or underutilized expenses.
            </p>
          </div>
        )}

        {/* Overall Net */}
        <div className="bg-gray-900 text-white rounded-xl p-6">
          <div className="text-xl font-semibold mb-4">Overall Net for {monthName}</div>
          
          {/* Expenses Summary */}
          <div className="mb-4 pb-4 border-b border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <div className="text-base font-medium text-gray-300">Expenses</div>
              <div className={`text-lg font-bold ${expenses.totalRemaining < 0 ? 'text-red-400' : expenses.totalRemaining === 0 ? 'text-gray-300' : 'text-green-400'}`}>
                {expenses.totalRemaining < 0 
                  ? `$${Math.abs(expenses.totalRemaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} over budget`
                  : expenses.totalRemaining === 0
                  ? '$0.00 (on budget)'
                  : `$${expenses.totalRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} under budget`}
              </div>
            </div>
          </div>

          {/* Savings Summary */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <div className="text-base font-medium text-gray-300">Savings</div>
              <div className={`text-lg font-bold ${overallSavings.totalRemaining > 0 ? 'text-red-400' : overallSavings.totalRemaining === 0 ? 'text-green-400' : 'text-green-400'}`}>
                {overallSavings.totalRemaining > 0
                  ? `$${overallSavings.totalRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} under saved`
                  : overallSavings.totalRemaining === 0
                  ? '$0.00 (fully saved)'
                  : `$${Math.abs(overallSavings.totalRemaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} over saved`}
              </div>
            </div>
          </div>

          {/* Overall Summary */}
          <div className="pt-4 border-t border-gray-700">
            <div className="flex justify-between items-center">
              <div className="text-base font-medium text-gray-300">Net Result</div>
              <div className={`text-xl font-bold ${overallNet < 0 ? 'text-red-400' : overallNet === 0 ? 'text-gray-300' : 'text-green-400'}`}>
                {overallNet < 0 
                  ? `$${Math.abs(overallNet).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} off budget`
                  : overallNet === 0 
                  ? '$0.00 (balanced)'
                  : `$${overallNet.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} net remaining`}
              </div>
            </div>
          </div>
        </div>

        {/* Close Button */}
        <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-white sticky bottom-0">
          <button
            onClick={handleClose}
            className="w-full py-3 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold text-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

